import React, { useState, useEffect, useRef } from 'react';
import { Bell, Plus, Check, CheckCheck, Users, CheckSquare, Mail, Calendar, Menu } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { getCurrentUser } from '@/lib/auth';
import { notificationsAPI, authAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useT } from '@/lib/LanguageContext';
import TimerPill from '@/components/time/TimerPill';

const buildMenuItems = (t) => [
  { label: t('topbar.new_contact'),    icon: Users,       path: '/contacts',  state: { openNew: true } },
  { label: t('topbar.new_task'),       icon: CheckSquare, path: '/tasks',     state: { openNew: true } },
  { label: t('topbar.new_sequence'),   icon: Mail,        path: '/sequences', state: { openNew: true } },
  { label: t('topbar.new_social_post'),icon: Calendar,    path: '/social',    state: { openNew: true } },
];

const TYPE_ICONS = {
  task_assigned:     '📋',
  task_completed:    '✅',
  sequence_enrolled: '📧',
  sequence_completed:'🎉',
  report_ready:      '📊',
};

export const TopBar = ({ title = 'Dashboard', onMenuClick }) => {
  const user = getCurrentUser();
  const navigate = useNavigate();
  const t = useT();
  const NEW_MENU_ITEMS = buildMenuItems(t);
  const userInitials = user?.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : (user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'HY');

  const getScopedLogo = () => {
    try {
      const u = JSON.parse(localStorage.getItem('heyday_user') || '{}');
      const companyId = u?.company_id;
      if (companyId) return localStorage.getItem(`heyday_company_logo_${companyId}`) || null;
      return null;
    } catch { return null; }
  };

  const [companyLogo, setCompanyLogo] = useState(getScopedLogo);

  // Listen for logo updates from Company Settings (same-tab, right after upload)
  useEffect(() => {
    const handler = () => { setCompanyLogo(getScopedLogo()); };
    window.addEventListener('heyday_company_updated', handler);
    return () => window.removeEventListener('heyday_company_updated', handler);
  }, []);

  // The localStorage copy is per-browser and can go stale or disappear (cache
  // clear, different device/browser). Fall back to the server's logo_url —
  // the actual source of truth — and refresh the local cache to match.
  useEffect(() => {
    authAPI.getMe().then(res => {
      const serverLogo = res.data?.company?.logo_url || null;
      if (serverLogo) {
        setCompanyLogo(prev => prev || serverLogo);
        try {
          const companyId = res.data?.company?.id;
          if (companyId) localStorage.setItem(`heyday_company_logo_${companyId}`, serverLogo);
        } catch {}
      }
    }).catch(() => {});
  }, []);

  const [panelOpen, setPanelOpen]         = useState(false);
  const [newMenuOpen, setNewMenuOpen]     = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(false);
  const panelRef  = useRef(null);
  const newMenuRef = useRef(null);

  // Poll unread count every 30s
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await notificationsAPI.unreadCount();
        setUnreadCount(res.data.count || 0);
      } catch {}
    };
    fetchCount();
    const id = setInterval(fetchCount, 30_000);
    return () => clearInterval(id);
  }, []);

  // Load full list when panel opens
  useEffect(() => {
    if (!panelOpen) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await notificationsAPI.list();
        setNotifications(res.data || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, [panelOpen]);

  // Close panels on outside click
  useEffect(() => {
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
      if (newMenuRef.current && !newMenuRef.current.contains(e.target)) {
        setNewMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMarkRead = async (id) => {
    try {
      await notificationsAPI.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {}
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleClickNotif = async (notif) => {
    if (!notif.read) await handleMarkRead(notif.id);
    if (notif.link) navigate(notif.link);
    setPanelOpen(false);
  };

  const fmtTime = (d) => {
    if (!d) return '';
    try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ''; }
  };

  return (
    <div
      className="sticky top-0 z-30 h-16 border-b px-6 flex items-center justify-between gap-4"
      style={{
        backgroundColor: 'hsl(var(--background) / 0.9)',
        backdropFilter: 'blur(8px)',
        borderColor: 'hsl(var(--border))',
      }}
    >
      <div className="flex items-center gap-3">
        {/* Hamburger — mobile only */}
        <button
          onClick={onMenuClick}
          className="md:hidden p-1.5 rounded-md hover:bg-gray-100 text-[#5C6670]"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="font-display text-lg md:text-2xl truncate" style={{ color: 'hsl(var(--foreground))' }}>
          {title}
        </h1>
      </div>

      <div className="flex items-center gap-3">
        {/* Time tracking timer (only when the Time module is on) */}
        <TimerPill />

        {/* Quick create */}
        <div className="relative hidden md:block" ref={newMenuRef}>
          <Button
            size="sm"
            className="flex"
            style={{ backgroundColor: '#A62B1F', color: 'white' }}
            onClick={() => setNewMenuOpen(o => !o)}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('topbar.new')}
          </Button>

          {newMenuOpen && (
            <div className="absolute right-0 top-10 w-52 bg-white rounded-xl shadow-xl border border-[#E3DDD1] z-50 overflow-hidden py-1">
              {NEW_MENU_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      setNewMenuOpen(false);
                      navigate(item.path, { state: item.state });
                    }}
                    className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#FBF8F2] transition-colors text-sm text-[#1E2A33]"
                  >
                    <Icon className="w-4 h-4 text-[#5C6670]" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Notification Bell */}
        <div className="relative" ref={panelRef}>
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setPanelOpen(o => !o)}
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-[#A62B1F] text-white text-[10px] font-bold flex items-center justify-center px-1">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>

          {panelOpen && (
            <div className="absolute right-0 top-12 w-screen max-w-sm md:w-96 bg-white rounded-xl shadow-xl border border-[#E3DDD1] z-50 overflow-hidden">
              {/* Panel header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-[#E3DDD1]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#1E2A33]" style={{ fontFamily: 'Manrope' }}>
                    {t('topbar.notifications')}
                  </span>
                  {unreadCount > 0 && (
                    <Badge className="bg-[#A62B1F] text-white text-xs px-1.5 py-0">
                      {unreadCount}
                    </Badge>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs text-[#5C6670] hover:text-[#1E2A33] flex items-center gap-1"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    {t('topbar.mark_all_read')}
                  </button>
                )}
              </div>

              {/* Notification list */}
              <div className="max-h-[420px] overflow-y-auto divide-y divide-[#F3EFE7]">
                {loading && (
                  <div className="py-8 text-center text-sm text-[#5C6670]">Loading…</div>
                )}
                {!loading && notifications.length === 0 && (
                  <div className="py-10 text-center">
                    <Bell className="w-8 h-8 text-[#D3CCBE] mx-auto mb-2" />
                    <p className="text-sm text-[#5C6670]">{t('topbar.no_notifications')}</p>
                  </div>
                )}
                {!loading && notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleClickNotif(n)}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-[#FBF8F2] transition-colors ${!n.read ? 'bg-[#FBF3EC]' : ''}`}
                  >
                    <span className="text-xl shrink-0 mt-0.5">
                      {TYPE_ICONS[n.type] || '🔔'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium ${!n.read ? 'text-[#1E2A33]' : 'text-[#5C6670]'}`}>
                          {n.title}
                        </p>
                        {!n.read && (
                          <span className="w-2 h-2 rounded-full bg-[#A62B1F] shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-[#5C6670] mt-0.5 line-clamp-2">{n.message}</p>
                      <p className="text-xs text-[#9AA3AB] mt-1">{fmtTime(n.created_at)}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Company logo / user avatar */}
        {companyLogo ? (
          <img
            src={companyLogo}
            alt="Company logo"
            style={{ height: '36px', width: 'auto', maxWidth: '120px', objectFit: 'contain' }}
            className="flex-shrink-0"
            onError={() => setCompanyLogo(null)}
          />
        ) : (
          <Avatar>
            <AvatarFallback className="bg-[#A62B1F] text-white">{userInitials}</AvatarFallback>
          </Avatar>
        )}
      </div>
    </div>
  );
};
